import axios from 'axios';
import { config } from '../config/config';
import { detectLocationFromMetadata } from '../utils/uaeIpDetector';

class FormSubmissionService {
  /**
   * Convert file to base64 string
   */
  fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Remove the data:image/jpeg;base64, prefix to get pure base64
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  }

  /**
   * Get IP geolocation information
   */
  async getIpInfo() {
    try {
      const response = await fetch('https://ipapi.co/json/', { timeout: 5000 });
      const data = await response.json();
      return {
        ip: data.ip || '',
        city: data.city || '',
        country: data.country_name || ''
      };
    } catch (error) {
      console.warn('Could not fetch IP info:', error);
      return {
        ip: '',
        city: '',
        country: ''
      };
    }
  }

  /**
   * Map form fields to workflow expected format
   * Maps internal form field names to the format expected by n8n workflow
   */
  mapFormFields(formData, ipInfo) {
    // Detect currency based on IP location
    const location = detectLocationFromMetadata(ipInfo);
    const detectedCurrency = location.currency; // 'AED' or 'USD'

    // Determine which salary fields to use based on detected currency
    const currentSalary = detectedCurrency === 'AED'
      ? (formData.gfaed_current || formData.aed_current || '')
      : (formData.usd_current || formData.gpusd_current || '');

    const expectedSalary = detectedCurrency === 'AED'
      ? (formData.gfaed_excepted || formData.aed_excepted || '')
      : (formData.usd_excepted || formData.gpusd_excepted || '');

    // Format date of birth if present (convert to YYYY-MM-DD format)
    let formattedAge = formData.gage || '';
    if (formattedAge && typeof formattedAge === 'string') {
      // If it's in DD/MM/YYYY format, convert to YYYY-MM-DD
      const dateParts = formattedAge.split('/');
      if (dateParts.length === 3) {
        formattedAge = `${dateParts[2]}-${dateParts[1].padStart(2, '0')}-${dateParts[0].padStart(2, '0')}`;
      }
    }

    return {
      // Personal Information (using hyphens as expected by workflow)
      'first-name': formData.first_name || formData.g1st_name || '',
      'last-name': formData.last_name || formData.g2nd_name || '',
      'email': formData.email || formData.gemail || '',
      'mobile': formData.whatsapp || formData.gwhatsapp || '', // whatsapp -> mobile

      // Position & Professional (arrays as expected)
      'position': formData.position ? [formData.position] : [],
      'position_record_id': formData.position_record_id || '', // Airtable record ID for position
      'last-position': formData.last_position || '',
      'experience-years': formData.how_many_years_of_experience_do_you_have || '',

      // Employment Details (arrays and renamed fields)
      'hiring-source': formData.hiring_source || formData.ghiring_source ?
        (Array.isArray(formData.hiring_source) ? formData.hiring_source : [formData.hiring_source || formData.ghiring_source]) : [],
      'full-time': formData.fullpart_time ? [formData.fullpart_time] : [],
      'join': formData.join || formData.gfjoin ? [formData.join || formData.gfjoin] : [],
      'current-salary': currentSalary,
      'expected-salary': expectedSalary,
      'user-currency': detectedCurrency, // Dynamically detected based on IP

      // Documents & Links
      'portfolio': formData.portfolio || '',
      'linkedin': formData.glinkedin || '',
      'instagram': formData.ginsta || '',

      // Education
      'education': formData.g_education || '',

      // Agreement (array as expected)
      'nda': formData.nda ? [String(formData.nda)] : ['false'],

      // Additional fields that may be in formData
      'gender': formData.gmf || '',
      'age': formattedAge, // Date in YYYY-MM-DD format
      'nationality': formData.nationality || '', // Record ID from Airtable
      'marital-status': formData.gfmarital || '',
      'residence': formData.where_do_you_currently_reside || '', // Record ID from Airtable
      'city': formData.gcity || '', // Record ID from Airtable
      'uae-residence': formData.uae_residence || '',
      'uae-years': formData.gfuae || '',
      'spoken-languages': formData.gspoken_languages || [], // Array of record IDs
      'written-languages': formData.gwritten_languages || [] // Array of record IDs
    };
  }

  /**
   * Map domain to valid Airtable Website Source values
   */
  mapDomainToWebsiteSource(hostname) {
    // Map localhost and development to production domain
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'astudio.ae';
    }

    // Map subdomains to main domains
    if (hostname.includes('astudio.ae')) {
      return 'astudio.ae';
    }
    if (hostname.includes('astudio.agency')) {
      return 'astudio.agency';
    }
    if (hostname.includes('astudio.dev')) {
      return 'astudio.dev';
    }

    // Default to astudio.ae if unknown
    return 'astudio.ae';
  }

  /**
   * Get metadata with IP geolocation
   */
  async getMetadata() {
    const urlParams = new URLSearchParams(window.location.search);
    const ipInfo = await this.getIpInfo();
    const hostname = window.location.hostname;

    return {
      utm_campaign: urlParams.get('utm_campaign') || '',
      utm_source: urlParams.get('utm_source') || '',
      utm_medium: urlParams.get('utm_medium') || '',
      utm_adid: urlParams.get('utm_adid') || '',
      ip: ipInfo.ip,
      city: ipInfo.city,
      country: ipInfo.country,
      browser: navigator.userAgent,
      device: /Mobile|Tablet/.test(navigator.userAgent) ? 'Mobile' : 'Desktop',
      landing_page: window.location.href,
      domain: this.mapDomainToWebsiteSource(hostname),
      referrer: document.referrer || '',
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Prepare files array with base64 encoding
   */
  async prepareFiles(files) {
    const filesArray = [];

    // Process each file
    for (const [fieldName, file] of Object.entries(files)) {
      if (file && file instanceof File) {
        try {
          const base64Content = await this.fileToBase64(file);
          filesArray.push({
            field_name: fieldName,
            filename: file.name,
            mime_type: file.type,
            content: base64Content
          });
        } catch (error) {
          console.error(`Error encoding file ${fieldName}:`, error);
        }
      }
    }

    return filesArray;
  }

  /**
   * Submit form with correct JSON structure for n8n workflow
   * @param {object} formData - Form data object
   * @param {object} files - Files object
   * @param {string} airtableRecordId - Airtable record ID (if updating existing record)
   */
  async submitForm(formData, files = {}, airtableRecordId = null) {
    try {
      // Get metadata with IP info first (needed for currency detection)
      const metadata = await this.getMetadata();

      // Map form fields to expected format (with IP-based currency detection)
      const form_data = this.mapFormFields(formData, metadata);

      // Prepare files as base64
      const filesArray = await this.prepareFiles(files);

      // Prepare request payload matching workflow expected format
      const payload = {
        form_data: form_data,
        metadata: metadata,
        files: filesArray
      };

      // Include Airtable record ID if provided (for updating existing record)
      if (airtableRecordId) {
        payload.record_id = airtableRecordId;
        payload.update_mode = true; // Flag to indicate this is an update operation
      }


      // Send as JSON (NOT multipart/form-data)
      const response = await axios.post(config.api.formSubmitWebhook, payload, {
        headers: {
          'Content-Type': 'application/json', // Changed from multipart/form-data
        },
        timeout: config.api.timeout,
      });

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Form submission error:', error);

      // Log detailed error for debugging
      if (error.response) {
        console.error('Response error:', {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers
        });
      }

      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to submit form'
      };
    }
  }
}

export default new FormSubmissionService();